import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router";
import { FaArrowLeft, FaCopy, FaExternalLinkAlt } from "react-icons/fa";
import toast from "react-hot-toast";
import API from "@/services/api";
import PublishToggle from "./components/publish-toggle";
import { useComponentTranslation } from "@/services/i18n";

const translations = {
  en: {
    back_to_channels: "Back to channels",
    link_copied: "Link copied to clipboard",
    share_link_label: "Share this job posting link:",
    copy_link: "Copy link",
  },
  fr: {
    back_to_channels: "Retour aux canaux",
    link_copied: "Lien copié dans le presse-papiers",
    share_link_label: "Partager le lien de cette offre d'emploi :",
    copy_link: "Copier le lien",
  },
};

export default function CustomChannel({ job, posts = [], setPosts }) {
  const { id: jobId, slug } = useParams();
  const navigate = useNavigate();
  const [channel, setChannel] = useState(null);
  const { t } = useComponentTranslation(translations);

  useEffect(() => {
    fetchChannel();
  }, [slug]);

  const fetchChannel = async () => {
    try {
      const response = await API.post(`/channel/search`, {
        slug: slug,
        limit: 1,
      });
      if (response.ok && response.data?.[0]) {
        setChannel(response.data[0]);
      }
    } catch (e) {
      console.error("Error fetching channel:", error);
    }
  };

  // Find job post from posts array
  const jobPost = channel ? posts.find((post) => post.channel_id === channel._id) : null;

  const shareLink = useMemo(() => {
    if (!job?.organization_slug || !jobPost?._id) return "";
    return `https://jobego.co/companies/${job.organization_slug}/jobs/${jobPost._id}`;
  }, [job, jobPost]);

  const handleCopyLink = () => {
    if (!shareLink) return;
    navigator.clipboard.writeText(shareLink);
    toast.success(t("link_copied"));
  };

  return (
    <div className="max-w-7xl mx-auto">
      <button onClick={() => navigate("..")} className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-4">
        <FaArrowLeft className="w-4 h-4" />
        {t("back_to_channels")}
      </button>

      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <div className="flex items-start gap-4">
          {channel?.logo ? (
            <div className="w-16 h-16 rounded-lg flex items-center justify-center shrink-0 overflow-hidden">
              <img src={channel.logo} alt={channel.name} className="w-full h-full object-cover" />
            </div>
          ) : (
            <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center shrink-0">
              <span className="text-2xl font-bold text-gray-600">{channel?.name?.charAt(0) || "?"}</span>
            </div>
          )}
          <div className="flex-1">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-2xl font-bold text-gray-900">{channel?.name || slug}</h3>
              {channel && <PublishToggle job={job} channel={channel} posts={posts} setPosts={setPosts} />}
            </div>
            {channel?.description && <p className="text-sm text-gray-600 mb-4">{channel.description}</p>}
            {channel?.url && (
              <a
                href={channel.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm text-primary hover:underline mb-6"
              >
                <FaExternalLinkAlt className="w-3 h-3" />
                {channel.url}
              </a>
            )}
            {jobPost && shareLink && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <p className="text-sm text-gray-700 mb-2">{t("share_link_label")}</p>
                <div className="flex items-center gap-2 bg-white border border-gray-200 rounded px-3 py-2">
                  <code className="text-sm text-gray-600 flex-1 truncate">{shareLink}</code>
                  <button onClick={handleCopyLink} className="p-1.5 hover:bg-gray-100 rounded transition-colors" title={t("copy_link")}>
                    <FaCopy className="w-4 h-4 text-gray-600" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
